import { useState } from "react";
import { Search, Check, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, Badge, TableWrap, Btn, Modal, Select, DateRange } from "../../components/admin/ui";

const PAGE_SIZES = [10, 50, 100, 500, 1000];

export default function Bookings({ paymentMode = false }) {
  const [filters, setFilters] = useState({ status: "all", payment_status: paymentMode ? "submitted" : "all", search: "", date_from: "", date_to: "" });
  const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v && v !== "all")).toString();
  const { data: bookings, loading, reload } = useFetch(`/admin/bookings?${qs}`, [qs]);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const [pageSize, setPageSize] = useState(10);
const [page, setPage] = useState(1);

const allRows = bookings || [];
const total = allRows.length;
const totalPages = Math.max(1, Math.ceil(total / pageSize));
const currentPage = Math.min(page, totalPages);
const start = (currentPage - 1) * pageSize;
const pagedRows = allRows.slice(start, start + pageSize);
  const open = async (b) => { setNote(""); setDetail(b); };

  const verify = async (action) => {
    try {
      const { data } = await api.post(`/admin/bookings/${detail.booking_id}/verify-payment`, { action, note });
      toast.success(action === "approve" ? "Payment verified, booking confirmed" : "Payment rejected");
      setDetail(data); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.post(`/admin/bookings/${detail.booking_id}/status`, { status, note });
      toast.success("Status updated"); setDetail(data); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const initiateRefund = async () => {
    try {
      const { data } = await api.post(`/admin/bookings/${detail.booking_id}/initiate-refund`, { note });
      toast.success("Refund initiated — track it under Refunds"); setDetail(data); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div>
      <PageTitle title={paymentMode ? "Payment Verification" : "Bookings"} desc={paymentMode ? "Review and verify submitted payment references" : "Search and manage all seva bookings"} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
          <input placeholder="Search by Booking ID, name, phone, seva, ref…" value={filters.search} onChange={(e) => set("search", e.target.value)} className="w-full rounded-lg bg-slate-800 border border-slate-700 pl-10 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="booking-search" />
        </div>
        <div className="w-44"><Select value={filters.status} onChange={(v) => set("status", v)} options={[{ value: "all", label: "All Statuses" }, "payment_pending", "payment_verification", "confirmed", "completed"/* , "cancelled", "rejected" */].map((x) => typeof x === "string" ? { value: x, label: x.replace(/_/g, " ") } : x)} testid="booking-status-filter" /></div>
       <div className="w-44"><Select value={filters.payment_status} onChange={(v) => set("payment_status", v)} options={[{ value: "all", label: "All Payments" }, "pending", "submitted", "paid", "failed"].map((x) => typeof x === "string" ? { value: x, label: x } : x)} testid="booking-payment-filter" /></div>
      </div>
      <div className="mb-4"><DateRange from={filters.date_from} to={filters.date_to} onChange={({ from, to }) => { setFilters((f) => ({ ...f, date_from: from, date_to: to })); setPage(1); }} testid="booking-date-range" /></div>

      <TableWrap>
        <thead className="bg-slate-800 text-slate-400"><tr>
          <th className="text-left px-4 py-3">Booking ID</th><th className="text-left px-4 py-3">Devotee</th><th className="text-left px-4 py-3">Seva</th><th className="text-left px-4 py-3">Date / Time</th><th className="text-left px-4 py-3">Amount</th><th className="text-left px-4 py-3">Payment</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3"></th>
        </tr></thead>
        <tbody className="divide-y divide-slate-700" data-testid="bookings-table">
          {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr> :
               pagedRows.map((b) => (
                <tr key={b.id} className="hover:bg-slate-800/50">
                <td className="px-4 py-3 font-semibold text-orange-400">{b.booking_id}</td>
                <td className="px-4 py-3">{b.devotee_name}<div className="text-xs text-slate-500">{b.phone}</div></td>
                <td className="px-4 py-3 text-slate-300">{b.seva_name}</td>
                <td className="px-4 py-3 text-slate-400">{b.date}<div className="text-xs">{b.time_slot}</div></td>
                <td className="px-4 py-3 text-orange-400 font-bold">₹{b.amount}</td>
                <td className="px-4 py-3"><Badge status={b.payment_status} /></td>
                <td className="px-4 py-3"><Badge status={b.booking_status} /></td>
                <td className="px-4 py-3 text-right"><button onClick={() => open(b)} className="p-2 rounded-lg hover:bg-slate-700 text-blue-400" data-testid={`booking-view-${b.booking_id}`}><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
          {!loading && (bookings || []).length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No bookings found</td></tr>}
        </tbody>
      </TableWrap>

      {!loading && total > 0 && (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-slate-300">
    <div className="flex items-center gap-2">
      <span>Show</span>
      <select
        value={pageSize}
        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
        className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200"
        data-testid="bookings-page-size"
      >
        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span>per page</span>
    </div>

    <span className="text-slate-400">
      {start + 1}–{Math.min(start + pageSize, total)} of {total}
    </span>

    <div className="flex items-center gap-2">
      <button
        onClick={() => setPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Prev</button>
      <span>{currentPage} / {totalPages}</span>
      <button
        onClick={() => setPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Next</button>
    </div>
  </div>
)}

      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Booking ${detail?.booking_id}`} wide>
        {detail && (
          <div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[["Devotee", detail.devotee_name], ["Phone", detail.phone], ["Email", detail.email || "—"], ["Gotra", detail.gotra || "—"],
                ["Seva", detail.seva_name], ["Persons", detail.persons || 1], ["Date", detail.date], ["Time", detail.time_slot], ["Amount", `₹${detail.amount}`],
                ["Payment Method", detail.payment_method || "—"], ["Payment Ref", detail.payment_reference || "—"],
                ["Address", detail.address || "—"], ["Special Request", detail.special_request || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-700 py-1.5"><span className="text-slate-400">{k}</span><span className="font-medium text-right">{v}</span></div>
              ))}
            </div>
            <div className="flex gap-3 mt-4">
              <Badge status={detail.payment_status} /><Badge status={detail.booking_status} />
              {detail.refund_status && detail.refund_status !== "not_eligible" && <Badge status={detail.refund_status} />}
            </div>

            <textarea placeholder="Add a note (optional)…" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full mt-4 rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-sm" data-testid="booking-note" />

            {detail.payment_status === "submitted" && (
              <div className="flex gap-2 mt-3">
                <Btn variant="success" onClick={() => verify("approve")} testid="verify-approve"><Check className="w-4 h-4" /> Verify & Confirm</Btn>
              {/* <Btn variant="danger" onClick={() => verify("reject")} testid="verify-reject"><X className="w-4 h-4" /> Reject Payment</Btn> */}
              </div>
            )}
            {detail.booking_status === "confirmed" && (
              <div className="flex gap-2 mt-3"><Btn variant="primary" onClick={() => changeStatus("completed")} testid="mark-completed">Mark as Completed</Btn></div>
            )}
          {/* {["payment_pending", "payment_verification", "confirmed"].includes(detail.booking_status) && (
  <div className="flex gap-2 mt-3"><Btn variant="danger" onClick={() => { if (window.confirm("Cancel this booking?")) changeStatus("cancelled"); }} testid="cancel-booking"><X className="w-4 h-4" /> Cancel Booking</Btn></div>
)} */}
            {["cancelled", "rejected"].includes(detail.booking_status) &&
  detail.payment_status === "paid" &&
  !["requested", "processing", "completed"].includes(detail.refund_status) && (
              <div className="flex gap-2 mt-3 items-center">
                <Btn variant="primary" onClick={initiateRefund} testid="initiate-refund">Initiate Refund (₹{detail.amount})</Btn>
                <span className="text-xs text-slate-400">Moves this booking to the Refunds list for processing.</span>
              </div>
            )}

            <div className="mt-5">
              <h4 className="font-semibold text-slate-300 mb-2">Audit History</h4>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {(detail.history || []).map((h, i) => (
                  <div key={i} className="text-xs text-slate-400 flex gap-2"><Badge status={h.status} /> <span>{h.note}</span></div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
